import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Wand2, Eye, EyeOff, Palette, ChevronDown, Plus, DollarSign, ArrowLeftRight } from 'lucide-react'
import type { Tier1VisualConfig } from '@/types'

interface Tier1VisualConfigSummaryProps {
  config: Tier1VisualConfig
}

export function Tier1VisualConfigSummary({ config }: Tier1VisualConfigSummaryProps) {
  const hasModifications = config.modifications && config.modifications.length > 0
  const hasToggles = config.elements_to_hide && config.elements_to_hide.length > 0
  const hasInsertions = config.elements_to_insert && config.elements_to_insert.length > 0
  const hasSwaps = config.elements_to_swap && config.elements_to_swap.length > 0
  const trimCents = config.template_config?.trim_cents
  const hasTrimCents = !!trimCents && trimCents.selectors.length + (trimCents.custom_selector ? 1 : 0) > 0
  const totalChanges = (config.modifications?.length || 0) + (config.elements_to_hide?.length || 0) + (config.elements_to_insert?.length || 0) + (config.elements_to_swap?.length || 0) + (hasTrimCents ? 1 : 0)

  // Group modifications by URL pattern
  const groupedModifications = React.useMemo(() => {
    if (!hasModifications) return {}
    return config.modifications.reduce((groups, mod, index) => {
      const pattern = mod.urlPattern || '/*'
      if (!groups[pattern]) {
        groups[pattern] = []
      }
      groups[pattern].push({ ...mod, originalIndex: index })
      return groups
    }, {} as Record<string, Array<typeof config.modifications[0] & { originalIndex: number }>>)
  }, [config.modifications, hasModifications])

  // Separate template-sourced toggles from manual ones, group by URL pattern
  const { groupedToggles, templateToggleGroups } = React.useMemo(() => {
    if (!hasToggles) return { groupedToggles: {}, templateToggleGroups: [] as Array<{ source: string; label: string; count: number; hideIn: string; urlPattern: string }> }

    const manual: typeof config.elements_to_hide = []
    const bySource: Record<string, typeof config.elements_to_hide> = {}

    config.elements_to_hide.forEach((toggle) => {
      const src = (toggle as any).source
      if (src) {
        if (!bySource[src]) bySource[src] = []
        bySource[src].push(toggle)
      } else {
        manual.push(toggle)
      }
    })

    // Group manual toggles by URL pattern (existing behavior)
    const grouped = manual.reduce((groups, toggle, index) => {
      const pattern = toggle.urlPattern || '/*'
      if (!groups[pattern]) groups[pattern] = []
      groups[pattern].push({ ...toggle, originalIndex: index })
      return groups
    }, {} as Record<string, Array<typeof config.elements_to_hide[0] & { originalIndex: number }>>)

    // Summarize template groups
    const sourceLabels: Record<string, string> = {
      'template:product_gallery': 'Galería de producto',
      'template:image_swap': 'Image Swap',
      'template:hero_banner': 'Hero Banner',  // Legacy — kept so old experiments still render
    }
    const tplGroups = Object.entries(bySource).map(([source, toggles]) => ({
      source,
      label: sourceLabels[source] || source,
      count: toggles.length,
      hideIn: toggles[0]?.hideIn || 'treatment',
      urlPattern: toggles[0]?.urlPattern || '/*',
    }))

    return { groupedToggles: grouped, templateToggleGroups: tplGroups }
  }, [config.elements_to_hide, hasToggles])

  const getPatternLabel = (pattern: string): string => {
    const labels: Record<string, string> = {
      '/': 'Homepage',
      '/*': 'Todas las páginas',
      '/productos/*': 'Páginas de productos',
      '/colecciones/*': 'Páginas de colecciones',
      '/carrito': 'Página del carrito',
      '/checkout/*': 'Páginas de checkout',
      '/cuenta/*': 'Páginas de cuenta',
      '/buscar': 'Página de búsqueda'
    }
    return labels[pattern] || pattern
  }

  // Group insertions by URL pattern
  const groupedInsertions = React.useMemo(() => {
    if (!hasInsertions) return {}
    return config.elements_to_insert!.reduce((groups, ins, index) => {
      const pattern = ins.urlPattern || '/*'
      if (!groups[pattern]) {
        groups[pattern] = []
      }
      groups[pattern].push({ ...ins, originalIndex: index })
      return groups
    }, {} as Record<string, Array<{ selector: string; urlPattern?: string; position: string; html?: string; asset_id?: string; description?: string; originalIndex: number }>>)
  }, [config.elements_to_insert, hasInsertions])

  if (!hasModifications && !hasToggles && !hasInsertions && !hasSwaps && !hasTrimCents) {
    return (
      <Card className="border-dashed">
        <CardContent className="text-center py-8">
          <Eye className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500 mb-1">No hay cambios guardados</p>
          <p className="text-xs text-slate-400">
            Usa el Editor Visual para crear modificaciones de estilo o toggles de elementos
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-indigo-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
              <Wand2 className="h-5 w-5 text-blue-600" />
              Configuración del Editor Visual
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              Guardado: {totalChanges} cambio{totalChanges !== 1 ? 's' : ''} aplicado{totalChanges !== 1 ? 's' : ''}
            </p>
          </div>
          <Badge className="bg-blue-600 hover:bg-blue-700 text-white">
            tier1_visual
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Style Modifications Section */}
        {hasModifications && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <Palette className="h-4 w-4 text-blue-600" />
              <h3 className="font-medium text-sm text-slate-700">
                Modificaciones de Estilo ({config.modifications.length})
              </h3>
            </div>

            <div className="space-y-3">
              {Object.entries(groupedModifications).map(([pattern, mods]) => (
                <details key={pattern} open className="group">
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-center gap-2 p-2 bg-white/70 hover:bg-white rounded-lg transition-all border border-slate-200/50">
                      <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-0 -rotate-90" />
                      <span className="text-sm font-medium text-slate-700">{getPatternLabel(pattern)}</span>
                      <Badge variant="outline" className="text-xs font-mono text-slate-500 ml-auto">
                        {mods.length}
                      </Badge>
                    </div>
                  </summary>

                  <div className="mt-2 ml-6 space-y-2">
                    {mods.map((mod) => (
                      <div key={mod.originalIndex} className="p-2.5 bg-white rounded-md border border-slate-200/70 shadow-sm">
                        <code className="text-xs font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded block truncate">
                          {mod.selector}
                        </code>
                        <p className="text-[10px] text-slate-500 mt-1.5">
                          {Object.keys(mod.changes).join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Element Toggles Section */}
        {hasToggles && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <EyeOff className="h-4 w-4 text-orange-600" />
              <h3 className="font-medium text-sm text-slate-700">
                Elementos Ocultos ({templateToggleGroups.length > 0
                  ? `${templateToggleGroups.length} template${Object.keys(groupedToggles).length > 0 ? ` + ${config.elements_to_hide.length - templateToggleGroups.reduce((s, g) => s + g.count, 0)} manual` : ''}`
                  : config.elements_to_hide.length})
              </h3>
            </div>

            {/* Template-sourced toggles: show as single summary card per template */}
            {templateToggleGroups.length > 0 && (
              <div className="space-y-2">
                {templateToggleGroups.map((group) => (
                  <div key={group.source} className="p-3 bg-white rounded-md border border-orange-200/70 shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700">{group.label}</span>
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200 ml-auto">
                        Oculto en {group.hideIn === 'control' ? 'A' : 'B'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {getPatternLabel(group.urlPattern)} — {group.count} selector{group.count !== 1 ? 'es' : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Manual toggles (non-template): show per-selector detail */}
            <div className="space-y-3">
              {Object.entries(groupedToggles).map(([pattern, toggles]) => (
                <details key={pattern} open className="group">
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-center gap-2 p-2 bg-white/70 hover:bg-white rounded-lg transition-all border border-slate-200/50">
                      <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-0 -rotate-90" />
                      <span className="text-sm font-medium text-slate-700">{getPatternLabel(pattern)}</span>
                      <Badge variant="outline" className="text-xs font-mono text-slate-500 ml-auto">
                        {toggles.length}
                      </Badge>
                    </div>
                  </summary>

                  <div className="mt-2 ml-6 space-y-2">
                    {toggles.map((toggle) => (
                      <div
                        key={toggle.originalIndex}
                        className="p-3 bg-white rounded-md border border-slate-200/70 shadow-sm"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-orange-700 bg-orange-50 px-2 py-1 rounded flex-1 truncate">
                              {toggle.selector}
                            </code>
                            <Badge
                              className={
                                toggle.hideIn === 'treatment'
                                  ? 'bg-orange-100 text-orange-800 border-orange-200'
                                  : 'bg-slate-100 text-slate-800 border-slate-200'
                              }
                            >
                              Oculto en {toggle.hideIn === 'treatment' ? 'B' : 'A'}
                            </Badge>
                          </div>
                          {toggle.description && (
                            <p className="text-xs text-slate-500 truncate">
                              {toggle.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Trim Cents Section */}
        {hasTrimCents && trimCents && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              <h3 className="font-medium text-sm text-slate-700">Trim Cents</h3>
            </div>
            <div className="p-3 bg-white rounded-md border border-emerald-200/70 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">
                  {trimCents.mode === 'zero_only' ? 'Solo centavos cero (,00 / .00)' : 'Todos los centavos (,XX / .XX)'}
                </span>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 ml-auto">
                  Variante {trimCents.variant === 'control' ? 'A' : 'B'}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {trimCents.selectors.length} selector{trimCents.selectors.length !== 1 ? 'es' : ''} predeterminado{trimCents.selectors.length !== 1 ? 's' : ''}
                {trimCents.custom_selector ? ` + 1 personalizado` : ''}
              </p>
              {trimCents.custom_selector && (
                <code className="text-[10px] font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded mt-1.5 inline-block">
                  {trimCents.custom_selector}
                </code>
              )}
            </div>
          </div>
        )}

        {/* In-place Image Swaps Section */}
        {hasSwaps && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <ArrowLeftRight className="h-4 w-4 text-purple-600" />
              <h3 className="font-medium text-sm text-slate-700">
                Image Swaps (in-place) ({config.elements_to_swap!.length})
              </h3>
            </div>
            <div className="space-y-2">
              {config.elements_to_swap!.map((swap, i) => (
                <div key={i} className="p-3 bg-white rounded-md border border-purple-200/70 shadow-sm">
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-purple-700 bg-purple-50 px-2 py-1 rounded flex-1 truncate">
                      {swap.selector}
                    </code>
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                      {swap.variant === 'control' ? 'A' : swap.variant === 'treatment' ? 'B' : swap.variant}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {getPatternLabel(swap.urlPattern || '/*')}
                    {(swap.asset_id_desktop || swap.image_url_desktop) ? ' — desktop' : ''}
                    {(swap.asset_id_mobile || swap.image_url_mobile) ? ' — mobile' : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Element Insertions Section */}
        {hasInsertions && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <Plus className="h-4 w-4 text-green-600" />
              <h3 className="font-medium text-sm text-slate-700">
                Elementos Insertados ({config.elements_to_insert!.length})
              </h3>
            </div>

            <div className="space-y-3">
              {Object.entries(groupedInsertions).map(([pattern, insertions]) => (
                <details key={pattern} open className="group">
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-center gap-2 p-2 bg-white/70 hover:bg-white rounded-lg transition-all border border-slate-200/50">
                      <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-0 -rotate-90" />
                      <span className="text-sm font-medium text-slate-700">{getPatternLabel(pattern)}</span>
                      <Badge variant="outline" className="text-xs font-mono text-slate-500 ml-auto">
                        {insertions.length}
                      </Badge>
                    </div>
                  </summary>

                  <div className="mt-2 ml-6 space-y-2">
                    {insertions.map((ins) => (
                      <div key={ins.originalIndex} className="p-2.5 bg-white rounded-md border border-slate-200/70 shadow-sm">
                        <code className="text-xs font-mono text-green-700 bg-green-50 px-2 py-0.5 rounded block truncate">
                          {ins.selector}
                        </code>
                        <p className="text-[10px] text-slate-500 mt-1.5">
                          Posición: {ins.position}{ins.description ? ` — ${ins.description}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
